import type {
  Account,
  Assumptions,
  ContributionRule,
  LargePurchase,
  Scenario,
  ScenarioMeta,
  Snapshot,
} from '../types';
import { uuid } from '../utils/uuid';

export interface SeedData {
  accounts: Account[];
  snapshots: Snapshot[];
  assumptions: Assumptions;
  purchases: LargePurchase[];
  scenarios: Scenario[];
  meta: ScenarioMeta;
}

/**
 * Illustrative first-run profile. Values are placeholders, not tied to any
 * real person — users are expected to overwrite everything from the UI on
 * first run.
 */
export function buildSeed(): SeedData {
  const now = new Date().toISOString();

  const accounts: Account[] = [
    mk('hysa', 'HYSA', 'hysa', 'hysa', null, 0),
    mk('checking', 'Checking', 'checking', null, null, 1),
    mk('retirement_401k', '401(k)', 'retirement', 'retirement', null, 2),
    mk('taxable_brokerage', 'Taxable Brokerage', 'taxable', 'taxable', null, 3),
    mk('home', 'Home', 'home', null, null, 4),
    mk('mortgage', 'Mortgage', 'mortgage', null, null, 5),
  ];

  const latestDate = new Date().toISOString().slice(0, 10);
  const snapshots: Snapshot[] = [
    {
      id: uuid(),
      date: latestDate,
      balances: {
        hysa: 25_000,
        checking: 5_000,
        retirement_401k: 50_000,
        taxable_brokerage: 20_000,
        home: 400_000,
        mortgage: 300_000,
      },
      notes: 'Starter snapshot',
      createdAt: now,
    },
  ];

  const assumptions: Assumptions = {
    id: 'singleton',
    currentAge: 30,
    targetRetirementAge: 65,
    startingCalendarYear: new Date().getFullYear(),
    // Legacy contribution fields kept populated for backwards compat with
    // anything reading them; the engine prefers `contributionRules` below.
    annualContribution401k: 23_000,
    annualContributionTaxable: 12_000,
    annualContributionHysa: 0,
    grossAnnualSalary: 120_000,
    compensation: {
      baseAnnual: 120_000,
      bonusAnnual: 0,
      bonusMonth: 2 as const,
    },
    contributionRules: [
      {
        id: 'seed-401k',
        name: '401(k) employee pretax',
        target: { kind: 'bucket', bucket: 'retirement' },
        mode: { kind: 'fixed_dollar', annual: 23_000 },
        schedule: { kind: 'lump_sum', month: 1 },
        taxTreatment: 'pretax',
        enabled: true,
        order: 0,
      },
      {
        id: 'seed-taxable',
        name: 'Taxable brokerage auto-invest',
        target: { kind: 'bucket', bucket: 'taxable' },
        mode: { kind: 'fixed_dollar', annual: 12_000 },
        schedule: { kind: 'monthly' },
        taxTreatment: 'after_tax_brokerage',
        enabled: true,
        order: 1,
      },
    ] satisfies ContributionRule[],
    stockRealReturn: 0.06,
    hysaRealReturn: 0.01,
    homeRealAppreciation: 0.005,
    swr: 0.04,
    inflation: 0.03,
    mortgageAnnualRate: 0.065,
    mortgageMonthlyPI: 1896,
    mortgageMonthlyEscrow: 400,
    monthlyHouseholdSpend: 5500,
    healthcareAnnual: 12_000,
    filingStatus: 'married',
    ltcgRate: 0.15,
    ordinaryRate: 0.22,
    stateTaxRate: 0.05,
    earlyWithdrawalPenalty: 0.1,
    homeCostBasis: 350_000,
    updatedAt: now,
  };

  const purchases: LargePurchase[] = [];

  const baseline: Scenario = {
    id: 'baseline',
    name: 'Baseline',
    description: 'Your current plan of record',
    icon: '🗻',
    color: '#3b54e8',
    order: 0,
    archived: false,
    events: [],
    createdAt: now,
  };

  const meta: ScenarioMeta = {
    id: 'singleton',
    activeScenarioId: 'baseline',
    compareScenarioIds: [],
    updatedAt: now,
  };

  return { accounts, snapshots, assumptions, purchases, scenarios: [baseline], meta };
}

function mk(
  id: string,
  name: string,
  kind: Account['kind'],
  bucket: Account['bucket'],
  realReturnOverride: number | null,
  order: number,
): Account {
  return {
    id,
    name,
    kind,
    bucket,
    realReturnOverride,
    order,
    archived: false,
    createdAt: new Date().toISOString(),
  };
}
